/**
 * This file contains utilities for using client hints for user preference which
 * are needed by the server, but are only known by the browser.
 */
import { getHintUtils, type ClientHint } from '@epic-web/client-hints'
import {
	clientHint as colorSchemeHint,
	subscribeToSchemeChange,
} from '@epic-web/client-hints/color-scheme'
import {
	clientHint as reducedMotionHint,
	subscribeToMotionChange,
} from '@epic-web/client-hints/reduced-motion'
import { clientHint as timeZoneHint } from '@epic-web/client-hints/time-zone'
import { useRevalidator } from '@remix-run/react'
import * as React from 'react'
import { useRequestInfo } from './request-info.ts'

const localeHint = {
	cookieName: 'CH-locale',
	getValueCode: `navigator.language`,
	fallback: 'en-US',
} satisfies ClientHint<string>
/**
 * Subscribe to changes in the user's locale preference. Optionally pass
 * in a cookie name to use for the cookie that will be set if different from the
 * default.
 */
export function subscribeToLocaleChange(
	subscriber: (value: string) => void,
	cookieName = localeHint.cookieName,
) {
	const schemaMatch = navigator.language
	function handleLanguageChange() {
		const value = schemaMatch
		document.cookie = `${cookieName}=${value}; Max-Age=31536000; Path=/`
		subscriber(value)
	}
	window.addEventListener('languagechange', handleLanguageChange)
	return function cleanupSchemaChange() {
		window.removeEventListener('languagechange', handleLanguageChange)
	}
}

const hintsUtils = getHintUtils({
	theme: colorSchemeHint,
	timeZone: timeZoneHint,
	locale: localeHint,
	reducedMotion: reducedMotionHint,
})

export const { getHints } = hintsUtils

/**
 * @returns an object with the client hints and their values
 */
export function useHints() {
	const requestInfo = useRequestInfo()
	return requestInfo.hints
}

/**
 * @returns inline script element that checks for client hints and sets cookies
 * if they are not set then reloads the page if any cookie was set to an
 * inaccurate value.
 */
export function ClientHintCheck({ nonce }: { nonce: string }) {
	const { revalidate } = useRevalidator()
	React.useEffect(() => {
		subscribeToSchemeChange(() => revalidate())
		subscribeToMotionChange(() => revalidate())
		subscribeToLocaleChange(() => revalidate())
	}, [revalidate])

	return (
		<script
			nonce={nonce}
			dangerouslySetInnerHTML={{
				__html: hintsUtils.getClientHintCheckScript(),
			}}
		/>
	)
}
